import { useCallback, useEffect, useState } from 'react';

const FOOTER_HEIGHT = 72; // Approximate footer height in pixels
const MARGIN = 25; // Margin from footer (matching Roster)

/**
 * Hook to calculate available height for dropdown content
 * based on the trigger element's position and panel footer
 */
export function useAvailableDropdownHeight(
  triggerRef: React.RefObject<HTMLElement>,
) {
  const [availableHeight, setAvailableHeight] = useState<number | null>(null);

  const calculateHeight = useCallback(() => {
    if (!triggerRef.current) {
      return;
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Calculate available space from bottom of trigger to viewport bottom,
    // minus footer height and margin
    const spaceBelow =
      viewportHeight - triggerRect.bottom - FOOTER_HEIGHT - MARGIN;

    // Ensure minimum reasonable height
    const minHeight = 200;
    const height = Math.max(spaceBelow, minHeight);

    setAvailableHeight(height);
  }, [triggerRef]);

  useEffect(() => {
    // Calculate on mount and when window resizes
    calculateHeight();

    window.addEventListener('resize', calculateHeight);
    window.addEventListener('scroll', calculateHeight, true);

    return () => {
      window.removeEventListener('resize', calculateHeight);
      window.removeEventListener('scroll', calculateHeight, true);
    };
  }, [calculateHeight]);

  return {
    availableHeight,
    recalculate: calculateHeight,
  };
}
